import { Button, Flex, Form, message, Select } from "antd";
import React from "react";
import moment from "moment";
import { SingleCounsellingData } from "../../types/WellnessCounsellingTypes";
import useSWR, { mutate } from "swr";
import { fetcher, patchPostFetcher } from "../../utils/fetcher";
import useSWRMutation from "swr/mutation";
import { Link } from "react-router-dom";
import { bookedSessionsListKey } from "../../constants/swrKeys";
import { AppModal } from "../../components/common";

type Props = {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  booking: SingleCounsellingData | null;
  onSessionsListUpdated?: () => void;
};

type AssignCounselorPayload = {
  session_id: string | undefined;
  counselor_id: string;
};

const API_URL = process.env.REACT_APP_API_URL;

export default function BookingInformationModal({
  isModalOpen,
  setIsModalOpen,
  booking,
  onSessionsListUpdated,
}: Props) {
  const [form] = Form.useForm();

  const { trigger: assignTrigger, isMutating: assignLoading } = useSWRMutation<
    any,
    any,
    string,
    AssignCounselorPayload
  >(`${API_URL}/session/update-counselor`, (url, { arg }) =>
    patchPostFetcher({
      url,
      data: arg,
      method: "PATCH",
    })
  );

  const { data: counselorData, isLoading: counselorLoading } = useSWR(
   `${API_URL}/counselor/getAllCounselor`,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      revalidateOnMount: true,
    }
  );
  console.log(counselorData,"counselor_company");
  

  const handleAssign = async (values: any) => {
    try {
      await assignTrigger({
        session_id: booking?._id,
        counselor_id: values.counselorId,
      });
      message.success({
        content: "Counselor assigned successfully",
      });
      await mutate(bookedSessionsListKey());
      onSessionsListUpdated?.();
      setIsModalOpen(false);
      form.resetFields();
    } catch (error: any) {
      message.error({
        content: error?.message,
      });
    }
  };
  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
  };
  

  const footer = (
    <Flex className="modalFooter">
      <Form
        onFinish={handleAssign}
        form={form}
        className="assignForm"
        layout="vertical"
      >
        <Form.Item
          name="counselorId"
          label={(booking?.previousCounselors?.length ?? 0) > 0 ? "Reassign Counselor" : "Assign Counselor"}
          rules={[{ required: true, message: "Please select a counselor" }]}
        >
          <Select
            loading={counselorLoading}
            placeholder="Please select the counselor"
            options={counselorData?.data?.map((item: any) => ({
              value: item._id,
              label: item?.full_name,
            }))}
          />
        </Form.Item>
        <Form.Item>
          <Button
            loading={assignLoading}
            htmlType="submit"
            className="assignBtn"
            type="primary"
          >
            Confirm & Assign
          </Button>
        </Form.Item>
      </Form>
    </Flex>
  );
const counselors = Array.isArray(booking?.previousCounselors)
  ? booking?.previousCounselors
  : booking?.previousCounselors
  ? [booking?.previousCounselors]
  : [];

  return (
    <AppModal
      footer={footer}
      className={"bookingModal"}
      title={`${booking?.user?._id ?? "Client ID"} - ${(booking?.previousCounselors?.length ?? 0) > 0 ? "Reassign Booking" : "Booking Confirmation"}`}
      open={isModalOpen}
      onCancel={handleCancel}
    >
      <Flex className="modalBody" vertical gap={8}>
        <p className="detailTitle">Client Details</p>
        <Flex className="details" gap={8} vertical>
          <Flex justify="space-between" className="">
            <p className="title">Booking ID</p>
            <p>{booking?._id}</p>
          </Flex>
          <Flex justify="space-between">
            <p className="title">Booking Date</p>
            <p>{moment(booking?.date).format("DD/MM/YYYY")}</p>
          </Flex>
          <Flex justify="space-between">
            <p className="title">Booking Time Slot</p>
            <p>{booking?.timeSlot}</p>
          </Flex>
          <Flex justify="space-between">
            <p className="title">Payment Status</p>
            <p>{booking?.isPaid ? "Paid" : "Unpaid"}</p>
          </Flex>
          <Flex justify="space-between">
            <p className="title">Session Status</p>
            <p>{booking?.sessionStatus}</p>
          </Flex>
          <Flex justify="space-between">
            <p className="title">Last Taken</p>
            <p>{booking?.prevDate ? moment(booking?.prevDate).format("DD/MM/YYYY") : "-"}</p>
          </Flex>
          <Flex justify="space-between">
            <p className="title">Session Number</p>
            <p>{booking?.sessionNumber}</p>
          </Flex>
          <Flex justify="space-between">
            <p className="title">Redflag Status</p>
            <p>{booking?.redFlagStatus === true ? "Yes" : "No"}</p>
          </Flex>
         {(booking?.previousCounselors?.length ?? 0) > 0 &&
          <Flex justify="space-between">
            <p className="title">Previous Counselor(s)</p>
            <p style={{paddingLeft: "10px"}}>{counselors?.join(", ") || "-"}</p>
          </Flex>}
        </Flex>
       
        <Link to={"/user-files-session-history"}>
          <Button
            className="viewClientBtn"
            type="default"
            style={{
              width: "100%"
            }}
            iconPosition="end"
          
            icon={
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clip-path="url(#clip0_352_1195)">
                  <path
                    d="M16.7031 6.5H16.6181V7.5H16.7031C16.8357 7.5 16.9629 7.55268 17.0567 7.64645C17.1504 7.74021 17.2031 7.86739 17.2031 8V18C17.2031 18.1326 17.1504 18.2598 17.0567 18.3536C16.9629 18.4473 16.8357 18.5 16.7031 18.5H7.70312C7.57052 18.5 7.44334 18.4473 7.34957 18.3536C7.2558 18.2598 7.20312 18.1326 7.20312 18V8C7.20312 7.86739 7.2558 7.74021 7.34957 7.64645C7.44334 7.55268 7.57052 7.5 7.70312 7.5H7.78813V6.5H7.70312C7.3053 6.5 6.92377 6.65804 6.64246 6.93934C6.36116 7.22064 6.20313 7.60218 6.20312 8V18C6.20312 18.3978 6.36116 18.7794 6.64246 19.0607C6.92377 19.342 7.3053 19.5 7.70312 19.5H16.7031C17.1009 19.5 17.4825 19.342 17.7638 19.0607C18.0451 18.7794 18.2031 18.3978 18.2031 18V8C18.2031 7.60218 18.0451 7.22064 17.7638 6.93934C17.4825 6.65804 17.1009 6.5 16.7031 6.5Z"
                    fill="#2E3191"
                    stroke="#2E3191"
                    stroke-width="0.3"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M15.7031 5.5H14.2031V4H10.2031V5.5H8.70312V8.5H15.7031V5.5ZM14.7031 7.5H9.70312V6.5H11.2031V5H13.2031V6.5H14.7031V7.5Z"
                    fill="#2E3191"
                    stroke="#2E3191"
                    stroke-width="0.3"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M9.20312 10.5H15.2031V11.5H9.20312V10.5ZM9.20312 13H15.2031V14H9.20312V13ZM9.20312 15.5H15.2031V16.5H9.20312V15.5Z"
                    fill="#2E3191"
                    stroke="#2E3191"
                    stroke-width="0.3"
                    stroke-linejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_352_1195">
                    <rect
                      width="16"
                      height="16"
                      fill="white"
                      transform="translate(4.20312 4)"
                    />
                  </clipPath>
                </defs>
              </svg>
            }
          >
            View Client Files & History
          </Button>
        </Link>
         
      </Flex>
        {(booking?.previousCounselors?.length ?? 0) > 0  && <Flex justify="space-between">
            <p className="ant-form-item_label">Assigned Counselor</p>
            <p>{booking?.previousCounselors[0]}</p>
          </Flex>}
    </AppModal>
  );
}
